import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, IconButton, MenuItem, Snackbar, Alert, TextField, Tooltip, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { CardWrapper, SectionTitle, formatBRL } from '../../../UI';
import * as C from '../../../../theme/colors.constants';
import DialogInstrucaoInicial from './DialogInstrucaoInicial';
import DialogCampanha from './DialogCampanha';
import DialogMemoriaDeCalculo from './DialogMemoriaDeCalculo';
import { ITEMS_ANALISE_ACORDO, TIPO_FORMA_PAGTO_DINHEIRO } from './passoDoisConstants';
import {
  ehFormaPagamentoComParcelaUnica, hojeMaisDias, makeFormFields, makePayloadRecalculo, makePayloadParcelas,
  buildParcelas, temAlerta, temThumbsDown,
} from './passoDoisController';
import { findRecalcularAcordo, findParcelasNovoAcordo } from '../../../../../services/core/processos/processosAcordosService';
import {
  findTiposFormaPagamentoAction, findTiposIntervaloPagamentoAction,
} from '../../../../../store/Global/LoadSelectors/loadSelectors.saga';

const MENSAGEM_ERRO_RECALCULAR = 'Não foi possível recalcular o acordo.';
const MENSAGEM_ERRO_PROXIMO = 'Não foi possível gerar as parcelas do acordo.';
const MENSAGEM_QUANTIDADE_INVALIDA = 'Quantidade de parcelas deve ser maior que 0.';
const MENSAGEM_MEMORIA_BLOQUEADA = 'A Memória de Cálculo não poderá ser gerada. Verifique as condições fornecidas.';

const LINHAS_RESUMO = [
  { id: 'valorSaldo', label: 'Valor Principal', sinal: '(+)', destaque: true },
  { id: 'valorProtesto', label: 'Protesto', sinal: '(+)' },
  { id: 'valorMulta', label: 'Multa', sinal: '(+)' },
  { id: 'valorSubTotal', label: 'SUBTOTAL', sinal: '(=)', destaque: true },
  { id: 'valorJuros', label: 'Juros/Desp.', sinal: '(+)' },
  { id: 'valorHonorarios', label: 'Honorários', sinal: '(+)' },
  { id: 'valorDesconto', label: 'Desconto', sinal: '(-)' },
  { id: 'valorTotal', label: 'Valor', sinal: '(=)', destaque: true },
];

/**
 * Passo 2 de Novo Acordo (calculadora de parcelas) — port de
 * `NovoAcordoPassoDois.jsx` real.
 *
 * `idProcessoRef` (latest ref) + captura do processo-alvo no início de cada
 * função assíncrona: mesmo guard já obrigatório desde a Etapa 4 sempre que um
 * componente vive dentro do painel "Novo Acordo" (que sobrevive à troca de
 * `idProcesso` sem desmontar — ver `src/prototipo/CLAUDE.md`).
 */
export default function NovoAcordoPassoDois({
  idProcesso, listaDeIds, dadosDefault, dadosResumo, setDadosResumo,
  setParcelas, setDataFinalPasso3, setDadosPadraoForm, onVoltar, onAvancar,
}) {
  const dispatch = useDispatch();
  const formaPagamentoOptions = useSelector(states => states.selectors.tiposFormaPagamento.selector);
  const intervaloOptions = useSelector(states => states.selectors.tiposIntervaloPagamento.selector);

  const [formFields, setFormFields] = useState(() => makeFormFields(dadosDefault));
  const [disabledButtonProximo, setDisabledButtonProximo] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [dialogInstrucaoAberto, setDialogInstrucaoAberto] = useState(false);
  const [dialogCampanhaAberto, setDialogCampanhaAberto] = useState(false);
  const [dialogMemoria, setDialogMemoria] = useState({ aberto: false, payload: null });

  const idProcessoRef = useRef(idProcesso);
  idProcessoRef.current = idProcesso;

  useEffect(() => {
    dispatch(findTiposFormaPagamentoAction());
    dispatch(findTiposIntervaloPagamentoAction());
  }, [dispatch]);

  useEffect(() => {
    setFormFields(makeFormFields(dadosDefault));
    setDisabledButtonProximo(false);
    // `carregando`/`erro` também precisam resetar aqui: o guard idProcessoRef
    // impede que uma resposta tardia do processo anterior corrompa o estado
    // atual, mas também significa que o `finally` daquela chamada nunca
    // desliga `carregando` para o processo NOVO — sem este reset, uma
    // eventual persistência futura de passo-por-processo (Etapa 7 do plano)
    // deixaria os botões travados em "carregando" ao reabrir este passo.
    setCarregando(false);
    setErro('');
  }, [dadosDefault]);

  const onChangeCampo = (event) => {
    const { name, value } = event.target;
    setFormFields(atual => ({ ...atual, [name]: value }));
    setDisabledButtonProximo(true);
  };

  const onChangeFormaPagamento = (event) => {
    const idTipoFormaPagto = Number(event.target.value);
    setFormFields((atual) => {
      const proximo = { ...atual, idTipoFormaPagto };
      if (ehFormaPagamentoComParcelaUnica(idTipoFormaPagto)) {
        proximo.dataPrimeiroVencimento = hojeMaisDias(0);
        proximo.idIntervaloPagamento = 0;
        proximo.quantidadeParcelas = 1;
      }
      return proximo;
    });
    setDisabledButtonProximo(true);
  };

  const onChangeIntervalo = (event) => {
    setFormFields(atual => ({ ...atual, idIntervaloPagamento: Number(event.target.value) }));
    setDisabledButtonProximo(true);
  };

  const desabilitaDataEIntervalo = ehFormaPagamentoComParcelaUnica(formFields.idTipoFormaPagto);
  const desabilitaQuantidadeParcelas = formFields.idTipoFormaPagto === TIPO_FORMA_PAGTO_DINHEIRO;
  const permiteDesconto = dadosDefault?.temCampanha === 'S';

  const executarRecalculo = async (ehMemoriaDeCalculo = false) => {
    if (Number(formFields.quantidadeParcelas) <= 0) {
      setErro(MENSAGEM_QUANTIDADE_INVALIDA);
      return { success: false, hasAlerta: false, payloadProposta: null };
    }

    const alvo = idProcesso;
    try {
      setCarregando(true);
      const payload = makePayloadRecalculo(formFields, listaDeIds, dadosDefault, ehMemoriaDeCalculo);
      const { data } = await findRecalcularAcordo(alvo, payload);
      if (idProcessoRef.current !== alvo) {
        return { success: false, hasAlerta: false, payloadProposta: null };
      }

      const payloadProposta = {
        valorSaldo: data.valorSaldo,
        valorJurosAbe: data.valorJurosAbe,
        valorJurosCliente: data.valorJurosCliente,
        valorHonorarios: data.valorHonorarios,
        valorProtesto: data.valorProtesto,
        valorMulta: data.valorMulta,
        valorDesconto: data.valorDesconto,
        valorTotal: data.valorTotal,
        valorJuros: data.valorJuros,
        valorSubTotal: data.valorSubTotal,
        jsonTitulos: data.jsonTitulos,
        porcentagemTaxaCartao: data.porcentagemTaxaCartao,
        valorTaxaCartao: data.valorTaxaCartao,
      };
      const jsonErros = JSON.parse(data.jsonErros)[0];
      const hasAlerta = temAlerta(jsonErros);
      const hasRestricao = hasAlerta || temThumbsDown(jsonErros);

      setDadosResumo({ ...data, jsonErros });
      setDisabledButtonProximo(false);

      if (ehMemoriaDeCalculo && hasRestricao) {
        setErro(MENSAGEM_MEMORIA_BLOQUEADA);
        return { success: true, hasAlerta: hasRestricao, payloadProposta: null };
      }
      return { success: true, hasAlerta, payloadProposta };
    } catch {
      if (idProcessoRef.current === alvo) {
        setErro(MENSAGEM_ERRO_RECALCULAR);
      }
      return { success: false, hasAlerta: false, payloadProposta: null };
    } finally {
      if (idProcessoRef.current === alvo) {
        setCarregando(false);
      }
    }
  };

  const onClickRecalcular = async (event) => {
    event.preventDefault();
    await executarRecalculo();
  };

  const onClickDemonstrativo = async () => {
    const { success, hasAlerta, payloadProposta } = await executarRecalculo(true);
    if (success && !hasAlerta) {
      setDialogMemoria({ aberto: true, payload: payloadProposta });
    }
  };

  const onClickProximo = async () => {
    if (Number(formFields.quantidadeParcelas) <= 0) {
      setErro(MENSAGEM_QUANTIDADE_INVALIDA);
      return;
    }

    const alvo = idProcesso;
    try {
      setCarregando(true);
      const payload = makePayloadParcelas(dadosResumo, alvo, formFields);
      const { data } = await findParcelasNovoAcordo(payload);
      if (idProcessoRef.current !== alvo) {
        return;
      }
      const parcelas = buildParcelas(data);
      if (parcelas.length === 0) {
        setErro(MENSAGEM_ERRO_PROXIMO);
        return;
      }
      setParcelas(parcelas);
      setDataFinalPasso3(parcelas[parcelas.length - 1].vencimento);
      setDadosPadraoForm({ ...formFields });
      onAvancar();
    } catch {
      if (idProcessoRef.current === alvo) {
        setErro(MENSAGEM_ERRO_PROXIMO);
      }
    } finally {
      if (idProcessoRef.current === alvo) {
        setCarregando(false);
      }
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Utilize a Calculadora para definir os novos valores negociados. Escolha também a forma de pagamento e a
        quantidade de parcelas do Acordo ou Simulação.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 0.85fr) minmax(0, 0.85fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          alignItems: 'start',
        }}
      >
        {/* --- Formulário --- */}
        <CardWrapper>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SectionTitle sx={{ mb: 0 }}>Instrução Inicial</SectionTitle>
            <Tooltip title={dadosDefault?.instrucaoInicial ? 'Veja a Instrução' : ''}>
              <span>
                <IconButton
                  size="small"
                  disabled={!dadosDefault?.instrucaoInicial}
                  onClick={() => setDialogInstrucaoAberto(true)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Forma de Pagamento"
              name="idTipoFormaPagto"
              value={formFields.idTipoFormaPagto}
              onChange={onChangeFormaPagamento}
              size="small"
              sx={{ flex: 1 }}
            >
              {formaPagamentoOptions.map(opcao => (
                <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Data 1º Vencimento"
              name="dataPrimeiroVencimento"
              value={formFields.dataPrimeiroVencimento}
              onChange={onChangeCampo}
              disabled={desabilitaDataEIntervalo}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              select
              label="Intervalo entre os Pagamentos"
              name="idIntervaloPagamento"
              value={formFields.idIntervaloPagamento}
              onChange={onChangeIntervalo}
              disabled={desabilitaDataEIntervalo}
              size="small"
              sx={{ flex: 1 }}
            >
              {intervaloOptions.map(opcao => (
                <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              label="Qtde. de Parcelas"
              name="quantidadeParcelas"
              value={formFields.quantidadeParcelas}
              onChange={onChangeCampo}
              disabled={desabilitaQuantidadeParcelas}
              size="small"
              inputProps={{ min: 1, max: 1000 }}
              sx={{ width: 140 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              type="number"
              label="Juros (%)"
              name="porcentagemJuros"
              value={formFields.porcentagemJuros}
              onChange={onChangeCampo}
              size="small"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="number"
              label="Honorários (%)"
              name="porcentagemHonorarios"
              value={formFields.porcentagemHonorarios}
              onChange={onChangeCampo}
              size="small"
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{ flex: 1 }}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <SectionTitle sx={{ mb: 0 }}>Campanha</SectionTitle>
            <Tooltip title={dadosDefault?.descricaoCampanha && dadosDefault?.temCampanha === 'S' ? 'Veja a Campanha' : ''}>
              <span>
                <IconButton
                  size="small"
                  disabled={!(dadosDefault?.descricaoCampanha && dadosDefault?.temCampanha === 'S')}
                  onClick={() => setDialogCampanhaAberto(true)}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <TextField
            type="number"
            label="Desconto (%)"
            name="porcentagemDesconto"
            value={permiteDesconto ? formFields.porcentagemDesconto : '0.00'}
            onChange={onChangeCampo}
            disabled={!permiteDesconto}
            size="small"
            inputProps={{ min: 0, max: 99, step: 0.01 }}
            sx={{ width: 160, mt: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button onClick={onVoltar}>Voltar</Button>
            <Button
              variant="contained"
              disabled={disabledButtonProximo || carregando}
              onClick={onClickProximo}
            >
              Próximo
            </Button>
          </Box>
        </CardWrapper>

        {/* --- Resumo --- */}
        <CardWrapper>
          <SectionTitle>Resumo</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {LINHAS_RESUMO.map(linha => (
              <Box key={linha.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ width: '55%', fontWeight: linha.destaque ? 700 : 400, color: C.black }}>
                  {linha.label}
                </Typography>
                <Typography sx={{ flex: 1, textAlign: 'right', fontWeight: linha.destaque ? 700 : 400 }}>
                  {dadosResumo?.[linha.id] == null ? '—' : formatBRL(dadosResumo[linha.id])}
                </Typography>
                <Typography variant="caption" sx={{ width: 22, color: C.warmGrey }}>{linha.sinal}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            <Button startIcon={<InfoOutlinedIcon />} onClick={onClickDemonstrativo} disabled={carregando}>
              Memória de Cálculo
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalculateOutlinedIcon />}
              onClick={onClickRecalcular}
              disabled={carregando}
            >
              Recalcular
            </Button>
          </Box>
        </CardWrapper>

        {/* --- Análise do Acordo --- */}
        <CardWrapper>
          <SectionTitle>Análise do Acordo</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {ITEMS_ANALISE_ACORDO.map((item) => {
              const jsonErros = dadosResumo?.jsonErros;
              const mensagem = jsonErros?.[item.messageProp];
              const cor = jsonErros?.[item.colorProp];
              const temRestricao = Boolean(jsonErros && mensagem && cor !== 'SUCESSO');
              const icone = !temRestricao ? (
                <ThumbUpAltIcon fontSize="small" sx={{ color: C.midGreen }} />
              ) : (
                <Tooltip title={mensagem}>
                  {cor === 'ALERTA' ? (
                    <ThumbUpAltIcon fontSize="small" sx={{ color: C.tangerine }} />
                  ) : (
                    <ThumbDownAltIcon fontSize="small" sx={{ color: C.tomato }} />
                  )}
                </Tooltip>
              );
              return (
                <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {icone}
                  <Typography variant="body2">{item.name}</Typography>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Atenção:</Typography>
            <Typography variant="caption">
              Propostas com algum tipo de restrição só serão liberadas com aprovação da ABE ou expressa autorização do cliente.
            </Typography>
          </Box>
        </CardWrapper>
      </Box>

      <DialogInstrucaoInicial
        open={dialogInstrucaoAberto}
        texto={dadosDefault?.instrucaoInicial}
        onClose={() => setDialogInstrucaoAberto(false)}
      />
      <DialogCampanha
        open={dialogCampanhaAberto}
        texto={dadosDefault?.descricaoCampanha}
        onClose={() => setDialogCampanhaAberto(false)}
      />
      <DialogMemoriaDeCalculo
        open={dialogMemoria.aberto}
        idProcesso={idProcesso}
        payloadProposta={dialogMemoria.payload}
        onClose={() => setDialogMemoria({ aberto: false, payload: null })}
      />
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Box>
  );
}

NovoAcordoPassoDois.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  listaDeIds: PropTypes.array,
  dadosDefault: PropTypes.object,
  dadosResumo: PropTypes.object,
  setDadosResumo: PropTypes.func.isRequired,
  setParcelas: PropTypes.func.isRequired,
  setDataFinalPasso3: PropTypes.func.isRequired,
  setDadosPadraoForm: PropTypes.func.isRequired,
  onVoltar: PropTypes.func.isRequired,
  onAvancar: PropTypes.func.isRequired,
};

NovoAcordoPassoDois.defaultProps = {
  idProcesso: null,
  listaDeIds: [],
  dadosDefault: {},
  dadosResumo: {},
};
